import { useState, type ReactNode } from 'react';
import { useApi, apiPost, Cargando, ErrorApi, Vacio, BotonAccion, formatU, formatFecha } from '../shared/ui.tsx';
import { exportarCSV, exportarPDF, type ColumnaTabla } from '../shared/exportar.ts';

const COL_LIQUIDACIONES: ColumnaTabla[] = [
  { clave: 'persona_apellido', etiqueta: 'Apellido' },
  { clave: 'persona_nombre', etiqueta: 'Nombre' },
  { clave: 'periodo_desde', etiqueta: 'Desde', formato: 'fecha' },
  { clave: 'periodo_hasta', etiqueta: 'Hasta', formato: 'fecha' },
  { clave: 'total', etiqueta: 'Total', formato: 'moneda' },
  { clave: 'estado', etiqueta: 'Estado' },
];

const COL_TURNOS: ColumnaTabla[] = [
  { clave: 'fecha', etiqueta: 'Fecha', formato: 'fecha' },
  { clave: 'categoria_nombre', etiqueta: 'Categoría' },
  { clave: 'horas_normales', etiqueta: 'H. normales', formato: 'numero' },
  { clave: 'horas_nocturnas', etiqueta: 'H. nocturnas', formato: 'numero' },
  { clave: 'pago_calculado', etiqueta: 'Pago base', formato: 'moneda' },
  { clave: 'ajuste_manual', etiqueta: 'Ajuste', formato: 'moneda' },
  { clave: 'total', etiqueta: 'Total', formato: 'moneda' },
];

export function LiquidacionesPage(): ReactNode {
  const [personaSel, setPersonaSel] = useState<string>('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [generandoPersona, setGenerandoPersona] = useState<string | null>(null);

  // personas para selector
  const personas = useApi<{ resultados: { id: string; nombre: string; apellido: string }[] }>('/api/personal?limite=200');
  // liquidaciones existentes
  const path = personaSel ? `/api/liquidaciones?persona_id=${personaSel}` : `/api/liquidaciones`;
  const { datos, cargando, error, recargar } = useApi<Array<{ id: string; persona_nombre: string; persona_apellido: string; periodo_desde: string; periodo_hasta: string; total: number; estado: string; pagada_en: string | null }>>(path);
  // turnos del período seleccionado
  const turnosPath = (personaSel && desde && hasta) ? `/api/turnos?persona_id=${personaSel}&periodo_desde=${desde}&periodo_hasta=${hasta}` : null;
  const turnos = useApi<Array<{ id: string; fecha: string; categoria_nombre: string; horas_normales: number; horas_nocturnas: number; pago_calculado: number; ajuste_manual: number | null; motivo_ajuste: string | null }>>(turnosPath);

  async function generar() {
    if (!personaSel || !desde || !hasta) { alert('Elegí persona y período'); return; }
    setGenerandoPersona(personaSel);
    try {
      const r = await apiPost<{ id: string; total: number; turnos: number }>('/api/liquidaciones', { persona_id: personaSel, periodo_desde: desde, periodo_hasta: hasta });
      alert(`Liquidación generada: $U ${r.total / 100} (${r.turnos} turnos)`);
      recargar();
    } catch (e) { alert(e instanceof Error ? e.message : 'Error'); }
    finally { setGenerandoPersona(null); }
  }

  async function pagar(id: string) {
    try { await apiPost(`/api/liquidaciones/${id}/pagar`, undefined, 'POST'); recargar(); }
    catch (e) { alert(e instanceof Error ? e.message : 'Error'); }
  }

  async function ajusteTurno(turnoId: string) {
    const aj = prompt('Ajuste (positivo o negativo, en pesos):');
    if (!aj) return;
    const monto = parseFloat(aj);
    if (Number.isNaN(monto)) { alert('Número inválido'); return; }
    const motivo = prompt('Motivo del ajuste (obligatorio):');
    if (!motivo) { alert('El motivo es obligatorio'); return; }
    try {
      await apiPost(`/api/turnos/${turnoId}/ajuste`, { ajuste_manual: Math.round(monto * 100), motivo_ajuste: motivo }, 'PUT');
      turnos.recargar();
    } catch (e) { alert(e instanceof Error ? e.message : 'Error'); }
  }

  function exportarLiq(formato: 'csv' | 'pdf') {
    if (!datos || datos.length === 0) return;
    const nombre = `liquidaciones_${desde || 'todo'}_${hasta || 'todo'}`;
    const preparados = datos.map((l) => ({
      ...l,
      total: l.total,
    }));
    if (formato === 'csv') exportarCSV(preparados, COL_LIQUIDACIONES, `${nombre}.csv`);
    else void exportarPDF(preparados, COL_LIQUIDACIONES, 'Liquidaciones', `${nombre}.pdf`);
  }

  function exportarTurnos(formato: 'csv' | 'pdf') {
    if (!turnos.datos || turnos.datos.length === 0) return;
    const nombre = `turnos_${personaSel}_${desde}_${hasta}`;
    const preparados = turnos.datos.map((t) => ({
      ...t,
      ajuste_manual: t.ajuste_manual ?? 0,
      total: t.pago_calculado + (t.ajuste_manual ?? 0),
    }));
    if (formato === 'csv') exportarCSV(preparados, COL_TURNOS, `${nombre}.csv`);
    else void exportarPDF(preparados, COL_TURNOS, `Turnos ${formatFecha(desde)} – ${formatFecha(hasta)}`, `${nombre}.pdf`);
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-neutro-800">Liquidaciones</h2>

      {/* Generar */}
      <section className="rounded-2xl bg-white p-4 shadow-sm border border-contorno-var">
        <h3 className="font-semibold text-neutro-800">Generar liquidación</h3>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <select className="campo" value={personaSel} onChange={(e) => setPersonaSel(e.target.value)}>
            <option value="">Elegir persona…</option>
            {personas.datos?.resultados.map((p) => <option key={p.id} value={p.id}>{p.apellido}, {p.nombre}</option>)}
          </select>
          <input className="campo" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          <input className="campo" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        <div className="mt-3">
          <BotonAccion onClick={generar} disabled={generandoPersona !== null} className="bg-primario-400 text-white">
            {generandoPersona ? 'Generando…' : 'Generar liquidación'}
          </BotonAccion>
        </div>
      </section>

      {/* Turnos del período */}
      {turnos.datos && turnos.datos.length > 0 && (
        <section className="rounded-2xl bg-white p-4 shadow-sm border border-contorno-var">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-neutro-800">Turnos del período ({turnos.datos.length})</h3>
            <div className="flex gap-1">
              <BotonAccion variante="fantasma" onClick={() => exportarTurnos('csv')} className="text-xs">CSV</BotonAccion>
              <BotonAccion variante="fantasma" onClick={() => exportarTurnos('pdf')} className="text-xs">PDF</BotonAccion>
            </div>
          </div>
          <ul className="mt-2 space-y-1 text-sm">
            {turnos.datos.map((t) => (
              <li key={t.id} className="flex items-center justify-between border-b border-contorno-var py-1.5">
                <div>
                  <p className="font-medium">{formatFecha(t.fecha)} · {t.categoria_nombre}</p>
                  <p className="text-xs text-neutro-400">
                    {t.horas_normales}h normal + {t.horas_nocturnas}h noct.
                    {t.ajuste_manual !== null && <span className="text-acento-600"> · ajuste {t.ajuste_manual > 0 ? '+' : ''}{formatU(t.ajuste_manual)}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-neutro-800">{formatU(t.pago_calculado + (t.ajuste_manual ?? 0))}</span>
                  <button className="text-xs text-primario-400 hover:underline" onClick={() => ajusteTurno(t.id)}>Ajustar</button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Liquidaciones existentes */}
      {cargando ? <Cargando /> : error ? <ErrorApi mensaje={error} onReintentar={recargar} /> : datos && datos.length > 0 ? (
        <section className="rounded-2xl bg-white p-4 shadow-sm border border-contorno-var">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-neutro-800">Liquidaciones</h3>
            <div className="flex gap-1">
              <BotonAccion variante="fantasma" onClick={() => exportarLiq('csv')} className="text-xs">CSV</BotonAccion>
              <BotonAccion variante="fantasma" onClick={() => exportarLiq('pdf')} className="text-xs">PDF</BotonAccion>
            </div>
          </div>
          <ul className="mt-2 space-y-2">
            {datos.map((l) => (
              <li key={l.id} className="flex items-center justify-between border-b border-contorno-var py-2">
                <div>
                  <p className="font-medium">{l.persona_apellido}, {l.persona_nombre}</p>
                  <p className="text-xs text-neutro-400">{formatFecha(l.periodo_desde)} – {formatFecha(l.periodo_hasta)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-neutro-800">{formatU(l.total)}</span>
                  <span className={`text-xs font-medium ${l.estado === 'pagada' ? 'text-estado-ok' : 'text-estado-pendiente'}`}>{l.estado}</span>
                  {l.estado === 'cerrada' && <button className="text-xs text-primario-400 hover:underline" onClick={() => pagar(l.id)}>Marcar pagada</button>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : <Vacio texto="Aún no hay liquidaciones generadas." />}
    </div>
  );
}